import { toast } from 'react-toastify';

import useInvestigationStore from '../InvestigationStore';
import {
  InvestigationCreateResponse,
  InvestigationMemosResponse,
  InvestigationResponse,
  InvestigationsResponse,
} from './zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { queryClient } from 'stateManagement/queries/client';

export const useCreateInvestigation_Mutation = () => {
  const { setCurrentInvestigation } = useInvestigationStore();

  return useMutation(
    /**
     * @param {Object} input
     * @param {import('./zod').IInvestigationCreateParams} input.investigationParams
     * @param {boolean} input.setAsCurrent
     */
    async (input) => {
      const response = await axios.post(
        '/api/v2/investigation/',
        input.investigationParams
      );

      const parseRes = InvestigationCreateResponse.safeParse(
        response.data.data
      );

      if (!parseRes.success) {
        throw new Error(`Failed to parse response: ${parseRes.error}`);
      }

      return parseRes.data;
    },
    {
      onSuccess: (newInvestigation, inputParams) => {
        const allInvestigations = queryClient.getQueryData(['investigations']);
        if (allInvestigations) {
          queryClient.setQueryData(['investigations'], (oldData) => [
            newInvestigation,
            ...oldData,
          ]);
        } else {
          queryClient.setQueryData(['investigations'], [newInvestigation]);
        }

        if (inputParams.setAsCurrent) {
          setCurrentInvestigation(newInvestigation._id, newInvestigation.name);
        }

        toast.success(`Investigation "${newInvestigation.name}" created`);
      },
      onError: () => toast.error('Failed to create investigation'),
    }
  );
};

export const useGetInvestigations_Query = () => {
  return useQuery(
    ['investigations'],
    async () => {
      const response = await axios.get('/api/v2/investigation/');
      const parseRes = InvestigationsResponse.safeParse(response.data.data);

      if (!parseRes.success) {
        throw new Error(`Failed to parse response: ${parseRes.error}`);
      }

      return parseRes.data;
    },
    {
      onError: () => toast.error('Failed to fetch investigations'),
      initialData: [],
    }
  );
};

/**
 *
 * @param {String} investigationId
 * @returns
 */
export const useGetInvestigationDetails_Query = (investigationId) => {
  return useQuery(
    ['investigations', investigationId],
    async () => {
      const response = await axios.get(
        `/api/v2/investigation/${investigationId}`
      );
      const parseRes = InvestigationResponse.safeParse(response.data.data);

      if (!parseRes.success) {
        throw new Error(`Failed to parse response: ${parseRes.error}`);
      }

      return parseRes.data;
    },
    {
      onError: () => toast.error('Failed to fetch investigations'),
      initialData: [],
    }
  );
};

/**
 *
 * @param {String} investigationId
 * @returns
 */
export const useGetInvestigationMemos_Query = (investigationId) => {
  return useQuery(
    ['investigations', 'memos', investigationId],
    async () => {
      const response = await axios.get(
        `/api/v2/investigation/${investigationId}/memos`
      );

      const parseRes = InvestigationMemosResponse.safeParse(response.data.data);

      if (!parseRes.success) {
        throw new Error(`Failed to parse response: ${parseRes.error}`);
      }

      return parseRes.data;
    },
    {
      onError: () => toast.error('Failed to fetch investigations'),
      cacheTime: 10 * 60 * 1000, // 10 minutes,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }
  );
};

/**
 *
 * @param {String} investigationId
 * @returns
 */
export const useGetInvestigationQueries_Query = (investigationId) => {
  return useQuery(
    ['investigations', 'queries', investigationId],
    async () => {
      const response = await axios.get(
        `/api/v2/investigation/${investigationId}/queries`
      );
      return response.data.data;
    },
    {
      onError: () => toast.error('Failed to fetch investigations'),
      cacheTime: 10 * 60 * 1000, // 10 minutes,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }
  );
};

export const useGetInvestigationTracers_Query = (investigationId) => {
  return useQuery(
    ['investigations', 'tracers', investigationId],
    async () => {
      const response = await axios.get(
        `/api/v2/investigation/${investigationId}/tracers`
      );
      return response.data.data;
    },
    {
      onError: () => toast.error('Failed to fetch investigations'),
      cacheTime: 10 * 60 * 1000, // 10 minutes,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }
  );
};

export const useDeleteInvestigation_Mutation = () => {
  return useMutation(
    /**
     * @param {string} investigationId
     */
    async (investigationId) => {
      await axios.delete(`/api/v2/investigation/${investigationId}`);
    },
    {
      // on success remove the investigation from the store
      onSuccess: (_output, investigationId) => {
        queryClient.setQueryData(['investigations'], (oldData) =>
          oldData.filter((inv) => inv._id !== investigationId)
        );

        toast.success('Investigation deleted');
      },
      onError: () => toast.error('Failed to delete investigation'),
    }
  );
};
